/**
 * Business-portal notification API helpers.
 *
 * Uses the same /api/notifications endpoints but forces the business token
 * via the X-Portal header so the apiClient interceptor picks the right JWT.
 */
import { apiClient } from '../../services/api';

const BASE = '/api/notifications';
const BIZ_HEADERS = { 'X-Portal': 'business' };

function getActiveBusinessId() {
  try {
    const raw = localStorage.getItem('business_session');
    const parsed = raw ? JSON.parse(raw) : null;
    const value = Number(parsed?.activeBusinessId || parsed?.businessId || 0);
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function withBusinessSettingsConfig(config = {}) {
  const businessId = getActiveBusinessId();
  const params = { ...(config.params || {}) };

  if (businessId) {
    params.businessId = businessId;
  }

  return {
    ...config,
    params,
    headers: { ...BIZ_HEADERS, ...(config.headers || {}) },
  };
}

export const getNotifications = async (params = {}) => {
  const { data } = await apiClient.get(BASE, { params, headers: BIZ_HEADERS });
  return data;
};

export const getUnreadCount = async () => {
  const { data } = await apiClient.get(`${BASE}/unread-count`, { headers: BIZ_HEADERS });
  return data.count;
};

export const markAsRead = async (id) => {
  const { data } = await apiClient.patch(`${BASE}/${id}/read`, {}, { headers: BIZ_HEADERS });
  return data;
};

export const markAllAsRead = async () => {
  const { data } = await apiClient.patch(`${BASE}/read-all`, {}, { headers: BIZ_HEADERS });
  return data;
};

/* ── Business settings API ──────────────────────────────── */

const SETTINGS_BASE = '/api/business/settings';
const DELIVERY_SETTINGS_BASE = '/api/business/delivery/settings/dispatch-location';
const DELIVERY_FEE_POLICY_BASE = '/api/business/delivery/settings';

export const getBusinessProfile = async () => {
  const { data } = await apiClient.get(`${SETTINGS_BASE}/profile`, withBusinessSettingsConfig());
  return data;
};

export const updateBusinessProfile = async (payload) => {
  const { data } = await apiClient.patch(`${SETTINGS_BASE}/profile`, payload, withBusinessSettingsConfig());
  return data;
};

export const getBusinessNotificationSettings = async () => {
  const { data } = await apiClient.get(`${SETTINGS_BASE}/notifications`, withBusinessSettingsConfig());
  return data;
};

export const updateBusinessNotificationPreferences = async (preferences) => {
  const { data } = await apiClient.patch(
    `${SETTINGS_BASE}/notifications/preferences`,
    { preferences },
    withBusinessSettingsConfig(),
  );
  return data;
};

export const getBusinessTrustOverview = async () => {
  const { data } = await apiClient.get(`${SETTINGS_BASE}/trust`, withBusinessSettingsConfig());
  return data;
};

export const submitBusinessTrustAppeal = async (reason) => {
  const businessId = getActiveBusinessId();
  const { data } = await apiClient.post(
    `${SETTINGS_BASE}/trust/appeals`,
    { businessId, reason },
    withBusinessSettingsConfig(),
  );
  return data;
};

export const getBusinessDispatchLocation = async () => {
  const { data } = await apiClient.get(DELIVERY_SETTINGS_BASE, withBusinessSettingsConfig());
  return data;
};

// What saving this location would do to unfinished deliveries, so the owner
// sees it before confirming. Read-only. The submitted location is sent so the
// server can classify the change by the same rules the save uses: a reworded
// address at the same pin moves nothing.
export const getDispatchLocationChangeImpact = async (location = null) => {
  const params = location ? {
    latitude: location.latitude, longitude: location.longitude,
    label: location.label, formattedAddress: location.formattedAddress,
    addressLine1: location.addressLine1, addressLine2: location.addressLine2,
    barangay: location.barangay, city: location.city, province: location.province,
    postalCode: location.postalCode, countryCode: location.countryCode,
  } : {};
  const { data } = await apiClient.get(`${DELIVERY_SETTINGS_BASE}/change-impact`, withBusinessSettingsConfig({ params }));
  return data?.data ?? data;
};

// Whether shoppers can order from this store yet, and what is missing. Any
// member can read it; canManage says whether this member can fix it.
export const getStorefrontDeliveryStatus = async () => {
  const { data } = await apiClient.get(`${DELIVERY_FEE_POLICY_BASE}/storefront-status`, withBusinessSettingsConfig());
  return data?.data ?? data;
};

export const putBusinessDispatchLocation = async (payload, idempotencyKey) => {
  const businessId = getActiveBusinessId();
  const { data } = await apiClient.put(
    DELIVERY_SETTINGS_BASE,
    { ...payload, businessId },
    withBusinessSettingsConfig({ headers: { 'Idempotency-Key': idempotencyKey } }),
  );
  return data;
};

// The rules a store commits work under: how many parcels one rider may carry,
// how long a rider has to answer a problem, and when someone is at the store to
// receive parcels and cash back. Created with the dispatch location.
export const getBusinessCommitmentPolicy = async () => {
  const { data } = await apiClient.get(
    `${DELIVERY_FEE_POLICY_BASE}/commitment-policy`,
    withBusinessSettingsConfig(),
  );
  return data;
};

// When trips can actually go out, per day: the store's receiving hours and the
// Riders' shifts side by side, their overlap and the latest new trip. Read-only.
export const getBusinessDispatchWindows = async () => {
  const { data } = await apiClient.get(
    `${DELIVERY_FEE_POLICY_BASE}/dispatch-windows`,
    withBusinessSettingsConfig(),
  );
  return data;
};

export const updateBusinessCommitmentPolicy = async (payload) => {
  const businessId = getActiveBusinessId();
  const { data } = await apiClient.put(
    `${DELIVERY_FEE_POLICY_BASE}/commitment-policy`,
    { ...payload, businessId },
    withBusinessSettingsConfig(),
  );
  return data;
};

export const getBusinessFeePolicies = async () => {
  const { data } = await apiClient.get(
    `${DELIVERY_FEE_POLICY_BASE}/fee-policy`,
    withBusinessSettingsConfig(),
  );
  return data;
};

export const createBusinessFeePolicyDraft = async (payload, idempotencyKey) => {
  const businessId = getActiveBusinessId();
  const { data } = await apiClient.post(
    `${DELIVERY_FEE_POLICY_BASE}/fee-policies`,
    { ...payload, businessId },
    withBusinessSettingsConfig({ headers: { 'Idempotency-Key': idempotencyKey } }),
  );
  return data;
};

export const activateBusinessFeePolicy = async (policyId, idempotencyKey) => {
  const businessId = getActiveBusinessId();
  const { data } = await apiClient.post(
    `${DELIVERY_FEE_POLICY_BASE}/fee-policies/${policyId}/activate`,
    { businessId },
    withBusinessSettingsConfig({ headers: { 'Idempotency-Key': idempotencyKey } }),
  );
  return data;
};
